import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { CCard, CCardBody } from "@coreui/react";
import DateRangeDropdown from "./DateRangeDropdown";

const PALETTE = [
  "#1E40AF",
  "#DC2626",
  "#16A34A",
  "#F97316",
  "#EAB308",
  "#9966FF",
  "#36A2EB",
  "#FF6384",
  "#4BC0C0",
  "#FFCE56",
];

// (a) Transactions by insurance company / insurance product — donut of
// successful transactions per group, with failed count + amount in tooltip.
const TransactionBreakdownCard = ({
  breakdownData,
  dateRange,
  setDateRange,
  breakdownBy,
  setBreakdownBy,
}) => {
  const chartRef = useRef(null);

  const rows =
    (breakdownData &&
      (breakdownBy === "product"
        ? breakdownData.byProduct
        : breakdownData.byInsurer)) ||
    [];

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }
    const chart = echarts.init(chartRef.current);
    const formatNumber = (num) => num.toLocaleString();
    const rowByName = rows.reduce((acc, row) => {
      acc[row.name] = row;
      return acc;
    }, {});
    const totalSuccess = rows.reduce(
      (sum, row) => sum + row.success_transactions,
      0
    );

    const option = {
      tooltip: {
        trigger: "item",
        formatter: (params) => {
          const row = rowByName[params.name] || {};
          return (
            `<strong>${params.name}</strong><br/>` +
            `Success: ${formatNumber(row.success_transactions || 0)}<br/>` +
            `Failed: ${formatNumber(row.failed_transactions || 0)}<br/>` +
            `Amount: RM ${(row.success_amount || 0).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          );
        },
      },
      grid: {
        bottom: "5%",
      },
      legend: {
        bottom: "0%",
        left: "center",
        align: "auto",
        type: "scroll",
        selectedMode: "multiple",
      },
      series: [
        {
          name: "Transactions",
          type: "pie",
          radius: ["40%", "70%"],
          avoidLabelOverlap: false,
          label: {
            show: false,
            position: "center",
          },
          labelLine: {
            show: false,
          },
          data: rows.map((row, index) => ({
            value: row.success_transactions,
            name: row.name,
            itemStyle: {
              color: PALETTE[index % PALETTE.length],
            },
          })),
        },
      ],
      graphic: {
        type: "group",
        left: "center",
        top: "center",
        children: [
          {
            type: "text",
            top: 0,
            style: {
              text: `${formatNumber(totalSuccess)}`,
              textAlign: "center",
              fill: "#252B36F2",
              fontSize: 20,
              fontWeight: "bolder",
            },
          },
          {
            type: "text",
            top: 30,
            style: {
              text: "Success",
              textAlign: "center",
              fill: "#252B36F2",
              fontSize: 14,
              fontWeight: "normal",
            },
          },
        ],
      },
    };

    chart.setOption(option);

    chart.on("legendselectchanged", function (params) {
      const selected = params.selected;
      const newTotal = rows.reduce((sum, row) => {
        return selected[row.name] ? sum + row.success_transactions : sum;
      }, 0);
      chart.setOption({
        graphic: {
          children: [
            {
              type: "text",
              top: 0,
              style: {
                text: `${formatNumber(newTotal)}`,
                textAlign: "center",
                fill: "#252B36F2",
                fontSize: 20,
                fontWeight: "bolder",
              },
            },
          ],
        },
      });
    });

    return () => {
      chart.dispose();
    };
  }, [breakdownData, breakdownBy]);

  return (
    <CCard className="border-light">
      <CCardBody style={{ overflowX: "auto" }}>
        <span>Transactions by Insurance Company / Product</span>
        <ul className="nav nav-pills mt-3">
          <li className="nav-item">
            <a
              className={`nav-link ${breakdownBy === "insurer" ? "active" : ""}`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setBreakdownBy("insurer");
              }}
            >
              By Company
            </a>
          </li>
          <li className="nav-item">
            <a
              className={`nav-link ${breakdownBy === "product" ? "active" : ""}`}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setBreakdownBy("product");
              }}
            >
              By Product
            </a>
          </li>
        </ul>
        <DateRangeDropdown dateRange={dateRange} setDateRange={setDateRange} />
        <div
          ref={chartRef}
          style={{ height: "400px", width: "100%", margin: "5px auto" }}
        ></div>
      </CCardBody>
    </CCard>
  );
};

export default TransactionBreakdownCard;
